/* eslint-disable @typescript-eslint/no-explicit-any */
export type JsonResponse<T = { [key: string]: any }> = T | T[];

export type AsyncJsonResponse<T = { [key: string]: any }> = Promise<
  JsonResponse<T>
> | null;

/**
 * Build an absolute `/api/...` request URL from a caller-supplied, possibly
 * user-derived `path`, pinned to `serverUrl`'s origin.
 *
 * Guards against server-side request forgery (code-scanning alert #43): a `path`
 * containing a scheme or authority (`http://evil`, `//evil`) is rejected, a
 * `path` containing a backslash (which URL parsing may treat as a path
 * separator) is rejected, and dot-segments (`.`/`..`) — which `new URL()` would
 * normalize and could use to climb out of the `/api/` prefix (e.g. `../admin`
 * → `/admin`) — are rejected. After construction the resulting URL's origin is
 * asserted to equal the configured server origin, and its pathname is asserted
 * to still live under `/api/`, before it is ever used. Legitimate paths —
 * including query strings (`.../links?type=file`), trailing slashes and
 * non-ASCII-safe segments such as email addresses (`users/lookup/a@b.com/`) —
 * are preserved unchanged.
 */
export function buildApiUrl(serverUrl: string, path: string): string {
  const trimmed = (path ?? '').trim();
  if (!trimmed) {
    throw new Error('Invalid API path');
  }

  // Reject absolute/protocol-relative paths up front. The `/api/` prefix below
  // already de-fangs these (they resolve into the path, not the authority), so
  // this is not what pins the host — it just fails loudly on a clearly bad path
  // instead of silently building `/api/http://evil.com`.
  if (/^(?:[a-zA-Z][a-zA-Z\d+\-.]*:)?\/\//.test(trimmed)) {
    throw new Error('Invalid API path');
  }

  // Reject backslashes: URL parsing can treat `\` as a path separator, so
  // `..\admin` or `\\evil` would otherwise smuggle traversal/authority past the
  // forward-slash checks below.
  if (trimmed.includes('\\')) {
    throw new Error('Invalid API path');
  }

  // Reject dot-segments. `new URL()` normalizes `.`/`..`, so a path like
  // `../admin` would resolve to `/admin` and escape the intended `/api/`
  // prefix. Splitting on `/` and rejecting any segment that is exactly `.` or
  // `..` blocks traversal while still preserving dots *inside* a segment (e.g.
  // `a@b.com`, `file.txt`).
  const relativePath = trimmed.replace(/^\/+/, '');
  const segments = relativePath.split('/');
  if (segments.some((segment) => segment === '.' || segment === '..')) {
    throw new Error('Invalid API path');
  }

  // Stripping leading slashes and prepending the constant `/api/` makes the
  // path an absolute-*path* reference, which can never carry an authority, so
  // the origin is always `serverUrl`'s. The origin assertion is therefore
  // unreachable at runtime; it is kept as the explicit host-allowlist barrier
  // CodeQL's request-forgery query recognizes, and as defense in depth.
  const url = new URL(`/api/${relativePath}`, serverUrl);
  if (url.origin !== new URL(serverUrl).origin) {
    throw new Error('Invalid API path');
  }

  // Defense in depth against dot-segment normalization escaping the prefix: the
  // resulting pathname must still live under `/api/`. If it does not, the path
  // climbed out (e.g. via traversal that slipped past the segment check) and we
  // fail loudly rather than issue a request to an unintended path.
  if (!url.pathname.startsWith('/api/')) {
    throw new Error('Invalid API path');
  }

  return url.toString();
}

export class ApiConnection {
  serverUrl: string;

  constructor(serverUrl: string) {
    if (!serverUrl) {
      throw Error('No Server URL provided.');
    }
    // using new URL() trims off excess whitespace and trailing '/'
    const u = new URL(serverUrl);
    this.serverUrl = u.origin;
  }

  toString(): string {
    return this.serverUrl;
  }

  async removeAuthToken() {
    await this.call('api/auth/oidc/logout');
  }

  /**
   * Makes a network call to the specified path.
   *
   * @template T - The expected shape of the response object. If not provided, defaults to any object shape.
   *
   * @param {string} path - The path of the API endpoint. (Should not include `/api/`.)
   * @param {Record<string, any>} body - Optional Request body to submit to the API.
   * @param {string} method - The HTTP Request method.
   * @param {Record<string, any>} headers - Optional Request headers to include.
   * @returns {AsyncJsonResponse<T>} Returns a Promise that resolves to the response data (or null).
   * @throws If `path` is empty or an absolute/protocol-relative URL (see {@link buildApiUrl}).
   *
   */
  // `fullResponse: true` returns the raw Response (required, so an omitted
  // options object resolves to the T | null overload, not Response).
  public async call(
    path: string,
    body: Record<string, any>,
    method: string,
    headers: Record<string, any>,
    options: Options & { fullResponse: true }
  ): Promise<Response>;
  public async call<T = { [key: string]: any }>(
    path: string,
    body?: Record<string, any>,
    method?: string,
    headers?: Record<string, any>,
    options?: Options & { fullResponse?: false }
  ): Promise<T | null>;
  public async call<T = { [key: string]: any }>(
    path: string,
    body: Record<string, any> = {},
    method: string = 'GET',
    headers: Record<string, any> = {},
    options?: Options
  ): Promise<Response | T | null> {
    const url = buildApiUrl(this.serverUrl, path);
    const refreshTokenUrl = buildApiUrl(this.serverUrl, 'auth/refresh');

    // Try to get OIDC access token and add to headers if available
    const requestHeaders = { ...headers };
    if (!requestHeaders['Authorization']) {
      try {
        // Dynamically import to avoid circular dependency
        const { useAuthStore } =
          await import('@send-frontend/stores/auth-store');
        const authStore = useAuthStore();
        const accessToken = await authStore.getAccessToken();

        if (accessToken) {
          requestHeaders['Authorization'] = `Bearer ${accessToken}`;
        }
      } catch (error) {
        // If we can't get the auth store (e.g., during initialization), continue without token
        console.debug('Could not get OIDC token for request:', error);
      }
    }

    const opts: Record<string, any> = {
      mode: 'cors',
      credentials: 'include', // include cookies for legacy auth
      method,
      headers: {
        'content-type': 'application/json',
        ...requestHeaders,
      },
    };

    if (method.trim().toUpperCase() === 'POST') {
      opts.body = JSON.stringify({
        ...body,
      });
    }

    let resp: Response;
    try {
      resp = await fetch(url, opts);
    } catch (e) {
      console.log(e);
      options?.onFailure?.({ kind: 'network', status: null, error: e });
      return null;
    }

    // Handle authentication errors. x-logout always rides on a 401, so branch on
    // it first and keep the two paths mutually exclusive — a revoked session
    // gets exactly one recovery attempt, never a second refresh from the plain
    // 401 branch below.
    if (resp.headers?.get?.('x-logout')) {
      // The backend flags a revoked OIDC session (logout / password change /
      // admin force-logout). Try to recover with the refresh token before
      // tearing down; recoverOrForceLogout forces logout only if the refresh
      // token is also dead, and keeps the session on a transient error (#974).
      try {
        const { useAuthStore } =
          await import('@send-frontend/stores/auth-store');
        const authStore = useAuthStore();
        const recovered = await authStore.recoverOrForceLogout();

        if (!recovered) {
          // Genuine logout (state already cleared) or a transient error (session
          // kept, fail open) — either way this request is done.
          return null;
        }

        // Session rolled forward — retry once with the freshly-rotated token.
        // We only reach here on a request that carried Authorization (the backend
        // emits x-logout solely for bearer-carrying requests), so no guard needed.
        const newToken = await authStore.getAccessToken();
        if (newToken) {
          opts.headers['Authorization'] = `Bearer ${newToken}`;
          resp = await fetch(url, opts);
        }
      } catch (error) {
        console.error('Forced-logout handling failed:', error);
        return null;
      }
    } else if (resp.status === 401) {
      // If we're using OIDC and get 401, try to refresh the token
      if (requestHeaders['Authorization']) {
        try {
          const { useAuthStore } =
            await import('@send-frontend/stores/auth-store');
          const authStore = useAuthStore();
          const newToken = await authStore.refreshToken();

          if (newToken) {
            // Retry with new token
            opts.headers['Authorization'] = `Bearer ${newToken}`;
            resp = await fetch(url, opts);
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          options?.onFailure?.({ kind: 'network', status: null, error });
          return null;
        }
      } else {
        // Legacy JWT refresh flow
        try {
          // Refresh token (including cookies)
          await fetch(refreshTokenUrl, {
            credentials: 'include',
            mode: 'cors',
          });
          resp = await fetch(url, opts);
        } catch (error) {
          console.log(error);
          options?.onFailure?.({ kind: 'network', status: null, error });
          return null;
        }
      }
    }

    if (!resp.ok) {
      // Surface the status/body for the caller's diagnostics before discarding
      // the response. Reading the body is safe here because we return null
      // (we never parse it as JSON on the error path).
      let body: string | undefined;
      try {
        body = (await resp.text()).slice(0, 500);
      } catch {
        body = undefined;
      }
      options?.onFailure?.({
        kind: 'http',
        status: resp.status,
        statusText: resp.statusText,
        body,
      });
      return null;
    }

    if (!!options?.fullResponse) {
      return resp;
    }

    return resp.json();
  }
}

/**
 * Why a {@link ApiConnection.call} failed. `call` returns `null` on every
 * failure, which loses the distinction between a network error and an API
 * 4xx/5xx (and the status code). Callers that need that for observability can
 * pass an `onFailure` hook to capture it.
 */
export type ApiCallFailure =
  | { kind: 'network'; status: null; error: unknown }
  | { kind: 'http'; status: number; statusText: string; body?: string };

type Options = {
  fullResponse?: boolean;
  /**
   * Optional diagnostics hook invoked when the call fails, just before `call`
   * returns null. Purely observational — it does not change the return value
   * or any control flow. Used to capture the HTTP status / network-vs-server
   * cause that would otherwise be discarded.
   */
  onFailure?: (failure: ApiCallFailure) => void;
};
